import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';

// Seconds on the success card before we move them into the portal on their own.
const REDIRECT_DELAY_MS = 2500;

/**
 * Page Stripe redirects back to after checkout.
 *
 * Landing here is not proof of payment — the session id in the URL only says a session was
 * created, and it can be typed by hand. So we ask the server to confirm the session with Stripe
 * before marking the Start flow complete; that same call converts the lead into a client and
 * hands back where they belong. Until it answers, nobody is told they're paid up.
 */
const CheckoutReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const isPortal = window.location.pathname.startsWith('/portal');

  // verifying | confirmed | processing | failed | missing
  const [status, setStatus] = useState(sessionId ? 'verifying' : 'missing');
  const [errorMessage, setErrorMessage] = useState('');
  const hasVerifiedRef = useRef(false);

  const goToPortal = useCallback(() => {
    navigate('/portal', { replace: true });
  }, [navigate]);

  const verify = useCallback(async () => {
    if (!sessionId || !currentUser) return;
    setStatus('verifying');
    setErrorMessage('');
    try {
      const verifyCheckoutSession = httpsCallable(functions, 'verifyCheckoutSession');
      const { data } = await verifyCheckoutSession({ sessionId });

      if (!data?.complete) {
        // Session exists but Stripe hasn't marked it complete — they backed out, or the bank
        // authorization is still in flight. Either way, no client conversion and no green check.
        setStatus('processing');
        return;
      }

      sessionStorage.removeItem('checkoutPersistedData');
      sessionStorage.removeItem('checkoutReturnToStep3');

      // Only now is the Start flow genuinely finished.
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const existing = userDoc.exists() ? userDoc.data()?.startFlowProgress || {} : {};
        await updateDoc(userRef, {
          startFlowProgress: {
            ...existing,
            checkoutCompleted: true,
            contractSigned: true,
            stripeSessionId: sessionId,
            lastUpdated: new Date().toISOString(),
          },
          lastUpdated: new Date().toISOString(),
        });
      } catch (error) {
        // The account is already converted server-side; this flag is only for the Start UI.
        console.error('Error saving checkout completion:', error);
      }

      setStatus('confirmed');
    } catch (error) {
      console.error('Error verifying checkout session:', error);
      setErrorMessage(error?.message || 'We could not confirm your payment.');
      setStatus('failed');
    }
  }, [sessionId, currentUser]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('missing');
      return;
    }
    if (!currentUser || hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;
    verify();
  }, [sessionId, currentUser, verify]);

  // Hand them off to the portal, where the pending-start gate takes over until their start date.
  useEffect(() => {
    if (status !== 'confirmed') return undefined;
    const timer = setTimeout(goToPortal, REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, goToPortal]);

  const buttonClass = 'px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors';

  const renderBody = () => {
    switch (status) {
      case 'verifying':
        return (
          <>
            <div className="flex justify-center mb-4">
              <BBALoader size={64} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Confirming your payment…
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Hang tight while we check this with Stripe.
            </p>
          </>
        );
      case 'confirmed':
        return (
          <>
            <div className="text-6xl mb-4">✓</div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Payment confirmed — welcome to BBA!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your contract is signed and your program is set up. Taking you to your portal…
            </p>
            <button onClick={goToPortal} className={buttonClass}>
              Go to My Portal
            </button>
          </>
        );
      case 'processing':
        return (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Payment not confirmed yet
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Stripe hasn't finished this checkout. If you just submitted your payment details,
              give it a moment and check again. If you closed the payment page, you can start
              checkout again.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={verify} className={buttonClass}>
                Check Again
              </button>
              <button
                onClick={() => navigate(isPortal ? '/portal/billing' : '/start/program')}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Back to Programs
              </button>
            </div>
          </>
        );
      case 'failed':
        return (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              We couldn't confirm your payment
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {errorMessage} If you were charged, nothing is lost — try again, or reach out to your
              BBA contact and we'll sort it out.
            </p>
            <button onClick={verify} className={buttonClass}>
              Try Again
            </button>
          </>
        );
      default:
        return (
          <>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
              Checkout
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              No session found. You may have cancelled the payment.
            </p>
            <button
              onClick={() => navigate(isPortal ? '/portal/billing' : '/start/program')}
              className={buttonClass}
            >
              Back to Programs
            </button>
          </>
        );
    }
  };

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative">
      <StartSidebar />
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
            {renderBody()}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default CheckoutReturn;
